import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import { calculateMonthlyPayment, calculateTotalInterest, formatCurrency } from '@/lib/calculators/formulas'

export default function MortgagePaymentCalculator() {
  const [purchasePrice, setPurchasePrice] = useState(200000)
  const [downPaymentPercent, setDownPaymentPercent] = useState(25)
  const [interestRate, setInterestRate] = useState(7)
  const [loanTerm, setLoanTerm] = useState(30)
  const [propertyTaxes, setPropertyTaxes] = useState(3000)
  const [insurance, setInsurance] = useState(1200)

  const downPayment = purchasePrice * (downPaymentPercent / 100)
  const loanAmount = purchasePrice - downPayment
  const monthlyPI = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)
  const monthlyTaxes = propertyTaxes / 12
  const monthlyInsurance = insurance / 12
  const totalMonthlyPayment = monthlyPI + monthlyTaxes + monthlyInsurance
  const totalInterest = calculateTotalInterest(loanAmount, monthlyPI, loanTerm)
  const totalCost = purchasePrice + totalInterest + propertyTaxes * loanTerm + insurance * loanTerm

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources/calculators" className="text-amber-600 hover:underline">
        &larr; Back to Calculators
      </Link>
      <h1 className="text-3xl font-bold">Mortgage Payment Calculator</h1>

      <Card>
        <CardContent className="space-y-4 p-6">
          <p>Calculate your monthly mortgage payment including principal, interest, taxes, and insurance.</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Property Details</h2>

            <CalculatorInput label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />

            <CalculatorInput
              label="Down Payment"
              value={downPaymentPercent}
              onChange={setDownPaymentPercent}
              suffix="%"
              tooltip="Investment properties typically require 20-25% down"
            />

            <div className="text-muted-foreground text-sm">
              Down payment: {formatCurrency(downPayment)} | Loan: {formatCurrency(loanAmount)}
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Loan Terms</h2>

            <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" />

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Loan Term</span>
              <div className="flex gap-2" role="group" aria-label="Loan Term">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setLoanTerm(term)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      loanTerm === term ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Annual Costs</h2>

            <CalculatorInput
              label="Property Taxes (Annual)"
              value={propertyTaxes}
              onChange={setPropertyTaxes}
              prefix="$"
            />

            <CalculatorInput label="Insurance (Annual)" value={insurance} onChange={setInsurance} prefix="$" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Monthly Payment Breakdown</h2>

            <CalculatorResult label="Total Monthly Payment" value={formatCurrency(totalMonthlyPayment)} highlight />

            <CalculatorResultsGrid>
              <CalculatorResult label="Principal & Interest" value={formatCurrency(monthlyPI)} />
              <CalculatorResult label="Property Taxes" value={formatCurrency(monthlyTaxes)} />
              <CalculatorResult label="Insurance" value={formatCurrency(monthlyInsurance)} />
              <CalculatorResult label="Loan Amount" value={formatCurrency(loanAmount)} />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Loan Summary</h2>

            <CalculatorResultsGrid>
              <CalculatorResult
                label="Total Interest Paid"
                value={formatCurrency(totalInterest)}
                subtext={`Over ${loanTerm} years`}
              />
              <CalculatorResult
                label="Total Cost"
                value={formatCurrency(totalCost)}
                subtext="Purchase + Interest + Taxes + Insurance"
              />
            </CalculatorResultsGrid>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
